/**
 * Fetch wrapper for same-origin /api calls.
 * - credentials:'include' ensures auth cookies are sent
 * - api.get / post / patch / put / delete: unwraps { data: T } → T (single resource or array)
 * - api.getPage: returns full paginated envelope { items, total, page, pageSize }
 *   Backend paginates as { data: T[], total, page, pageSize }
 * - On { error } response → throws ApiError with structured message
 * - On 401 redirects to /login (session expired)
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
};

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const base = path.startsWith('/') ? path : `/${path}`;
  if (!params) return base;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) qs.set(k, String(v));
  }
  const qstr = qs.toString();
  return qstr ? `${base}?${qstr}` : base;
}

async function rawFetch(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<unknown> {
  const { body, params, headers: extraHeaders, ...rest } = options;
  const url = buildUrl(path, params);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(extraHeaders as Record<string, string>),
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...rest,
  });

  // Session expired
  if (res.status === 401) {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
    throw new ApiError(401, 'Unauthorized');
  }

  // Parse JSON (best-effort)
  let data: unknown;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const dataObj = data as Record<string, unknown> | null;
    // Handle { error: { code, message } } or { error: string } or { message: string }
    const errorField = dataObj?.['error'];
    const message =
      (typeof errorField === 'object' && errorField !== null
        ? (errorField as Record<string, string>)['message']
        : typeof errorField === 'string'
          ? errorField
          : undefined) ??
      (typeof dataObj?.['message'] === 'string' ? (dataObj['message'] as string) : undefined) ??
      `HTTP ${res.status}`;
    throw new ApiError(res.status, message, data);
  }

  return data;
}

/**
 * Single/array resource: always unwraps { data: T } → T.
 * Use for: GET single, GET array sub-resources, POST, PATCH, PUT, DELETE.
 */
async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const data = await rawFetch(method, path, options);

  if (data !== null && typeof data === 'object' && 'data' in (data as object)) {
    return (data as { data: T }).data;
  }
  return data as T;
}

/**
 * Paginated list: returns { items, total, page, pageSize }.
 * Backend shape: { data: T[], total, page, pageSize }.
 */
async function requestPage<T>(
  path: string,
  params?: RequestOptions['params'],
): Promise<PageResult<T>> {
  const data = await rawFetch('GET', path, { params });

  if (
    data !== null &&
    typeof data === 'object' &&
    'data' in (data as object) &&
    Array.isArray((data as Record<string, unknown>)['data'])
  ) {
    const env = data as { data: T[]; total: number; page: number; pageSize: number };
    return { items: env.data, total: env.total, page: env.page, pageSize: env.pageSize };
  }

  // Fallback: already in items shape (future-proofing)
  if (data !== null && typeof data === 'object' && 'items' in (data as object)) {
    return data as PageResult<T>;
  }

  throw new ApiError(500, 'Unexpected paginated response shape', data);
}

export const api = {
  /** GET with { data: T } unwrap — for single resources and array sub-resources */
  get: <T>(path: string, params?: RequestOptions['params']) =>
    request<T>('GET', path, { params }),

  /** GET paginated list → { items, total, page, pageSize } */
  getPage: <T>(path: string, params?: RequestOptions['params']) =>
    requestPage<T>(path, params),

  post: <T>(path: string, body?: unknown) =>
    request<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>('PATCH', path, { body }),
  put: <T>(path: string, body?: unknown) =>
    request<T>('PUT', path, { body }),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
